import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Company } from './company';
import { CompanyVendor } from './company-vendor';
import { ExemptVendor } from './exempt-vendor';
import { Vendor } from './vendor';

export class BusinessVerificationApiError extends BaseModel {
  static BusinessVerificationApiErrorProperties: (
    | {
        property: keyof BusinessVerificationApiError;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof BusinessVerificationApiError;
        type: 'alias';
      }
    | {
        property: keyof BusinessVerificationApiError;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'ClientRegisteredVendorInvoiceId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'requestParameters', type: 'column', dataType: 'string' },
    { property: 'errorResponseJSON', type: 'column', dataType: 'string' },
    { property: 'errorDescription', type: 'column', dataType: 'string' },
    { property: 'nextRunDate', type: 'column', dataType: 'date' },
    { property: 'retryCount', type: 'column', dataType: 'number' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    { property: 'isSuccessful', type: 'column', dataType: 'boolean' },
    { property: 'apiType', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'vendorType', type: 'alias' },
    { property: 'vendorLegalName', type: 'alias' },
    { property: 'clientLegalName', type: 'alias' },

    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' }
  ];

  static {
    BusinessVerificationApiError.BusinessVerificationApiErrorProperties.forEach(
      (propertyObject) => {
        const { property, type } = propertyObject;
        if (type === 'column') {
          BusinessVerificationApiError.columnMap.set(property, propertyObject);
        }
        if (type === 'alias') {
          BusinessVerificationApiError.aliasedColumnMap.set(
            property,
            propertyObject as { property: string; type: 'alias' }
          );
        }
        if (type === 'association') {
          BusinessVerificationApiError.associationMap.set(
            property,
            propertyObject as {
              property: string;
              type: 'association';
              model: string;
            }
          );
        }
      }
    );
  }

  constructor(
    BusinessVerificationApiErrorObject: Partial<BusinessVerificationApiError>
  ) {
    super();

    BusinessVerificationApiError.BusinessVerificationApiErrorProperties.forEach(
      (propertyObject) => {
        const { property } = propertyObject;
        const value = BusinessVerificationApiErrorObject[property];
        if (value !== undefined) {
          Object.assign(this, { [property]: value });
        }
      }
    );
  }

  id?: number;
  CompanyId?: number;
  VendorId?: number;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  ClientRegisteredVendorInvoiceId?: number | null;
  requestParameters?: string | null;
  errorResponseJSON?: string;
  errorDescription?: string | null;
  nextRunDate?: Date | null;
  retryCount?: number;
  isArchived?: boolean;
  isSuccessful?: boolean;
  apiType?:
    | (
        | 'Tin Check'
        | 'IRS Tin Match'
        | 'Lexis Nexis V2'
        | 'Lexis Nexis Legacy'
        | 'Lexis Nexis Corporate Filing'
        | 'Lexis Nexis Corporate Filing Report'
        | 'Lexis Nexis Person Lookup'
      )
    | null;
  createdAt?: Date;
  updatedAt?: Date;

  vendorType?: string;
  vendorLegalName?: string;
  clientLegalName?: string;

  Company?: Company;
  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
}
